import { Component, inject, OnInit, signal } from '@angular/core';
import { PrintersModuleService } from '../../services/printers-module.service';
import { MainBaseComponent } from 'src/app/shared/components/master-detail-layout/main-base/main-base.component';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { GeneralSettingsFormGroups, GeneralSettingsSections } from '../../models/settings-printers.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';
import { MatButtonModule } from '@angular/material/button';
import { FiscalSettingsComponent } from './fiscal-settings/fiscal-settings.component';
import { OrderSettingsComponent } from './order-settings/order-settings.component';
import { GeneralSettingsFormService } from '../../services/general-settings-form.service';
import { mobileCheck } from 'src/utilities';
import { ConfigurationManagerService, ConfigurationPreferences, LoaderService } from 'src/app/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AlertDialogService } from 'src/app/dialogs';
import { PrinterNavigationStateService } from '../../services/printer-navigation-state.service';
import { $state } from 'app/ajs-upgraded-providers';

@Component({
  selector: 'app-general-settings',
  standalone: true,
  imports: [ 
    CommonModule,
    MainBaseComponent,
    ActionBtnComponent,
    FiscalSettingsComponent,
    OrderSettingsComponent,
    MatTabsModule,
    MatButtonModule,
    TranslateModule
  ],
  templateUrl: './general-settings.component.html',
  styleUrl: './general-settings.component.scss'
})
export class GeneralSettingsComponent implements OnInit {

  private readonly printersModuleService = inject(PrintersModuleService);
  private readonly generalSettingsFormService = inject(GeneralSettingsFormService);
  private readonly checkManagerService = inject(ConfigurationManagerService);
  private readonly translateService = inject(TranslateService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly loaderService = inject(LoaderService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly printerNavigationStateService = inject(PrinterNavigationStateService);
  private readonly state = inject($state);
  
  protected preferences = signal<any>(null);
  protected generalSettingsForm!: GeneralSettingsFormGroups;

  tabChange: number = 0;
  activeTab: GeneralSettingsSections | null = 'fiscal';

  protected isMobile = mobileCheck();

  ngOnInit() {
    this.loadPreferences();
    this.printersModuleService.updateSelection("GENERAL", {generalSettings: this.preferences()})
    this.generalSettingsForm = this.generalSettingsFormService.createForm(this.preferences());
    this.printersModuleService.scrollToStart();
  }

  loadPreferences() {
    const data = this.printersModuleService.getGeneralPreferences();
    this.preferences.set(data);
  }

  async save() {
    // always use the live form: the service may have recreated it after ngOnInit
    const form = this.generalSettingsFormService.form;
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    const currentFormValues = form.value;
    const originalPreferences = this.preferences();
    const updatedPreferences = structuredClone(originalPreferences);

    const processUpdates = async (section: 'fiscalprinter' | 'nofiscalprinter') => {
      const currentSection = currentFormValues[section];
      const originalSection = originalPreferences[section];

      if (!currentSection || !originalSection) return;

      for (const [key, value] of Object.entries(currentSection)) {
        // unset preferences read back as null: compare against the form default instead
        const originalValue = originalSection[key] ?? (typeof value === 'boolean' ? false : '');
        if (value !== originalValue) {
          this.checkManagerService.setShopPreference(`${section}.${key}` as keyof ConfigurationPreferences, value);
          updatedPreferences[section][key] = value;
        }
      }
    };

    this.loaderService.show();
    try {
      await Promise.all([
        processUpdates('fiscalprinter'),
        processUpdates('nofiscalprinter')
      ]);

      this._snackBar.open(this.translateService.instant('SETTINGS.SETTINGS.SAVE_EDIT_SUCCESSFUL'),
        undefined, 
        { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        }
      );

      this.preferences.set(updatedPreferences); 
      this.printersModuleService.updateGeneralPreferences(updatedPreferences);
      this.printerNavigationStateService.initialData = structuredClone(this.generalSettingsFormService.form?.getRawValue());

      form.markAsPristine();
    } catch (error) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('SETTINGS.SETTINGS.SAVE_EDIT_ERROR')
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  async goBack() {
    const confirmed = await this.printerNavigationStateService.checkPendingData();
    if (!confirmed) return;

    this.printersModuleService.updateSelection(undefined);
    this.printersModuleService.updateShowDetailMobile(false);
    this.state.go('app.new.settings.printers', { checkPendingData: false });
  }
}
